import {
  formatMetricDisplayScore,
  getDisplayValue,
  getProgressPercentage,
  normalizeMetricValue,
} from "@Components/common/byb-ds/cards/byb-metric-card.utils";
import BybVrCard from "@Components/common/byb-ds/cards/byb-vr-card";
import MetricDetailsDialog from "@Components/common/metrics/metric-details-dialog";
import ScanRateScoreMaxedIndicator from "@Components/common/scan-rate/scan-rate-score-maxed-indicator";
import { AppContext } from "@Context/common/general/context";
import { useSessionResultsScenarioData, useSessionResultsTabContext } from "@Context/game/activity/session-results-context";
import AnalyticsEvents from "@Enums/AnalyticsEvents";
import MetricEventTypes from "@Enums/MetricEventTypes";
import MetricFilterTypes from "@Enums/MetricFilterTypes";
import { getPerformanceShade } from "@Helpers/feedback";
import { useAnalytics } from "@hooks/common/analytics/use-analytics";
import { IPerformanceStats } from "@hooks/game/activity/session/use-session-results";
import { useTrainingFocus } from "@hooks/game/progress/use-training-focus";
import useI18n from "@i18n";
import { Box, Button, LinearProgress, Skeleton, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { isScanRateScoreMaxed } from "@Shared/utilities/scanScoreUtilities";
import { useContext, useState } from "react";

const MetricsSection = () => {
  const theme = useTheme();
  const { i18n } = useI18n();
  const { state } = useContext(AppContext);
  const { scenarioData } = useSessionResultsScenarioData();
  const { currentTab } = useSessionResultsTabContext();
  const { track } = useAnalytics();
  const { trainingFocus } = useTrainingFocus();
  const [selectedMetric, setSelectedMetric] = useState<MetricEventTypes | null>(null);
  const focusMetric = trainingFocus?.metric as string | undefined;

  const creationDate = state.user.creationDate instanceof Date ? state.user.creationDate.getTime() / 1000 : state.user.creationDate;
  const metricsToRender =
    scenarioData?.performanceStats?.filter(
      (metric: IPerformanceStats) => metric.type !== MetricFilterTypes.BYB_IQ && (metric.type !== MetricFilterTypes.DECISION || currentTab === 0)
    ) || [];

  if (!scenarioData) {
    return (
      <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gridTemplateRows="repeat(2, 1fr)" gap={theme.spacing(3)} height="100%">
        {Array.from({ length: 6 }, (_, index) => (
          <Box key={index}>
            <BybVrCard>
              <Stack width="100%" height="100%" alignItems="stretch" justifyContent="space-between" paddingBottom={1.5}>
                <Skeleton variant="text" width={125} height={40} animation="wave" />
                <Stack direction="row" justifyContent="space-between" alignItems="baseline" width="100%">
                  <Skeleton variant="text" width={80} height={42} animation="wave" />
                  <Skeleton variant="text" width={50} height={42} animation="wave" />
                </Stack>
                <Skeleton variant="rounded" width="100%" height={30} animation="wave" />
              </Stack>
            </BybVrCard>
          </Box>
        ))}
      </Box>
    );
  }

  const openMetricDetails = (metric: MetricEventTypes) => {
    track(AnalyticsEvents.metric_details_clicked, {
      metric,
      is_focus: focusMetric === metric,
      user_rating: state.user.userRating,
    });
    setSelectedMetric(metric);
  };

  return (
    <>
      <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gridTemplateRows="repeat(2, 1fr)" gap={theme.spacing(3)} height="100%">
        {metricsToRender.map((metric: IPerformanceStats) => {
          const score = metric.score || 0;
          const progress = metric.progress || 0;
          const target = metric.target || 0;
          const normalizedDisplayScore = getDisplayValue(metric.type, score) ?? 0;
          const formattedDisplayScore = formatMetricDisplayScore(metric.type, normalizedDisplayScore);
          const progressPercentage = getProgressPercentage(metric.type, progress) ?? 0;
          const targetPercentage = getProgressPercentage(metric.type, target) ?? 0;
          const difference = progressPercentage - targetPercentage;
          const isFocusMetric = focusMetric === metric.type;

          const normalizedScore = normalizeMetricValue(metric.type, score) ?? 0;
          const color = getPerformanceShade(metric.type, normalizedScore, theme, creationDate);

          return (
            <Box key={metric.type}>
              <BybVrCard
                onClick={() => openMetricDetails(metric.type as unknown as MetricEventTypes)}
                highlight={isFocusMetric}
                highlightColor={color.v300}
              >
                <Stack width="100%" height="100%" alignItems="stretch" justifyContent="space-between" paddingBottom={1.5}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                    <Typography variant="text1xl500" color="text.secondary" textAlign="left">
                      {i18n(`metric.${metric.type}.name`)}
                    </Typography>
                    {!isFocusMetric && metric.type === MetricFilterTypes.SCAN_FREQUENCY && isScanRateScoreMaxed(score) && (
                      <ScanRateScoreMaxedIndicator />
                    )}
                    {isFocusMetric && (
                      <Button data-shape="pill" size="small" color="secondary" sx={{ color: color.v300 }}>
                        <Typography variant="textBase400" color={color.v300}>
                          Focus
                        </Typography>
                      </Button>
                    )}
                  </Stack>

                  {progress !== null && (
                    <>
                      <Box display="flex" flex={1} alignItems="center" justifyContent="center">
                        <Stack direction="row" justifyContent="space-between" alignItems="baseline" width="100%">
                          <Stack direction="row" alignItems="baseline" spacing={0.5}>
                            <Typography variant="text3xl700" color="text.primary">
                              {formattedDisplayScore}
                            </Typography>
                            <Typography variant="textLg500" color="text.secondary">
                              {i18n(`metric.${metric.type.toLowerCase()}.short`)}
                            </Typography>
                          </Stack>
                          <Typography variant="textLg500" color={difference >= 0 ? "dsGreen.v200" : "dsRed.v200"}>
                            {difference >= 0 ? "+" : ""}
                            {difference.toFixed(1)}%
                          </Typography>
                        </Stack>
                      </Box>

                      <Box position="relative" width="100%">
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(progressPercentage, 100)}
                          sx={{
                            height: 18,
                            borderRadius: 8,
                            border: `${theme.spacing(0.4)} solid ${theme.palette.dsBase.v200}08`,
                            backgroundColor: `${theme.palette.dsBase.v200}08`,
                            backgroundClip: "padding-box",
                            "& .MuiLinearProgress-bar": {
                              background: `linear-gradient(to right,
                              ${color.v100} 0%, 
                              ${color.v200} 75%, 
                              ${color.v300} 100%
                            )`,
                              borderRadius: 8,
                            },
                          }}
                        />
                        {target > 0 && (
                          <Box
                            position="absolute"
                            left={`${Math.min(targetPercentage, 100)}%`}
                            bottom={-12}
                            sx={{
                              transform: "translateX(-50%)",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              pointerEvents: "none",
                            }}
                          >
                            <svg width={10} height={10} viewBox="0 0 10 10">
                              <polygon
                                points="5,4.5 1,9 9,9"
                                fill={theme.palette.dsNeutral.v300}
                                stroke={theme.palette.dsNeutral.v300}
                                strokeWidth="2"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </Box>
                        )}
                      </Box>
                    </>
                  )}
                </Stack>
              </BybVrCard>
            </Box>
          );
        })}
      </Box>
      <MetricDetailsDialog
        isTrainingFocus={focusMetric === selectedMetric}
        metric={selectedMetric}
        open={selectedMetric !== null}
        onClose={() => setSelectedMetric(null)}
      />
    </>
  );
};

export default MetricsSection;
